// Advisory: detect only clear, long, straight cuts INSIDE the final portrait.
// Normal shoulders and clothing touching its frame must never trigger this.
// Inspect local source alpha, never the brand overlay.
export function detectRenderedShoulderCrop({
  pixels, width, height, face, foreground,
  scale, photoX, photoY, canvasWidth = 605, canvasHeight = 688,
}) {
  const clear = { warning: false, sides: [] };
  const validBox = (box) => Boolean(box && Number.isFinite(box.x)
    && Number.isFinite(box.y) && Number.isFinite(box.width)
    && Number.isFinite(box.height) && box.width > 0 && box.height > 0);
  if (!pixels || !Number.isInteger(width) || !Number.isInteger(height)
    || width < 32 || height < 32 || pixels.length < width * height * 4
    || !Number.isFinite(scale) || scale <= 0
    || !Number.isFinite(photoX) || !Number.isFinite(photoY)
    || !Number.isFinite(canvasWidth) || !Number.isFinite(canvasHeight)
    || canvasWidth < 32 || canvasHeight < 32) return clear;

  // A missing face only permits a conservative foreground-based estimate.
  const resolvedFace = validBox(face) ? face
    : validBox(foreground) ? {
      x: foreground.x + foreground.width * 0.32,
      y: foreground.y + foreground.height * 0.05,
      width: foreground.width * 0.36,
      height: foreground.height * 0.30,
    } : null;
  if (!resolvedFace) return clear;

  const faceBottom = resolvedFace.y + resolvedFace.height;
  const faceCenter = resolvedFace.x + resolvedFace.width / 2;
  const fromY = Math.max(0, Math.ceil(faceBottom + resolvedFace.height * 0.10));
  const toY = Math.min(height - 1, Math.floor(faceBottom + resolvedFace.height * 3.5));
  if (toY - fromY < Math.max(12, resolvedFace.height * 0.3)) return clear;

  const photoLeft = photoX - width * scale / 2;
  const photoTop = photoY - height * scale / 2;
  const drawnFaceBottom = photoTop + faceBottom * scale;
  const drawnFaceHeight = resolvedFace.height * scale;
  const drawnFaceCenter = photoLeft + faceCenter * scale;
  const sourceStep = Math.max(1, Math.floor((toY - fromY) / 180));
  const center = Math.max(0, Math.min(width - 1, Math.round(faceCenter)));
  const seedRange = Math.max(2, Math.round(resolvedFace.width * 0.18));
  const gapAllowance = Math.max(1, Math.min(4, Math.floor(width / 450)));
  const minBodyWidth = Math.max(8, Math.min(width * 0.15, resolvedFace.width * 0.60));
  const minLayerWidth = Math.max(8, Math.ceil(width * 0.035));
  const rows = [];

  for (let sourceY = fromY; sourceY <= toY; sourceY += sourceStep) {
    const offset = (sourceY * width) * 4 + 3;
    const isPerson = (x) => pixels[offset + x * 4] >= 170;
    let seed = -1;
    for (let d = 0; d <= seedRange; d++) {
      if (center - d >= 0 && isPerson(center - d)) { seed = center - d; break; }
      if (center + d < width && isPerson(center + d)) { seed = center + d; break; }
    }
    if (seed < 0) continue;

    // Follow the central torso, never the leftmost stray matte pixel or a
    // disconnected shemagh tail on the same scanline.
    function limit(direction) {
      let last = seed;
      let gap = 0;
      for (let x = seed + direction; x >= 0 && x < width; x += direction) {
        if (isPerson(x)) { last = x; gap = 0; }
        else if (++gap > gapAllowance) break;
      }
      return last;
    }
    // A shemagh/shoulder layer may be separated from the central torso by
    // transparent gaps. A horizontal center-out flood can miss that whole
    // layer on one side. Include substantial outer runs, not thin fringe.
    function outerLimits() {
      let first = null;
      let last = null;
      let x = 0;
      while (x < width) {
        if (!isPerson(x)) { x++; continue; }
        const start = x;
        let lastOpaque = x;
        let gaps = 0;
        x++;
        while (x < width) {
          if (isPerson(x)) { lastOpaque = x; gaps = 0; }
          else if (++gaps > gapAllowance) break;
          x++;
        }
        if (lastOpaque - start + 1 >= minLayerWidth) {
          if (first === null) first = start;
          last = lastOpaque;
        }
        x = Math.max(x + 1, lastOpaque + 1);
      }
      return { first, last };
    }
    const bodyLeft = limit(-1);
    const bodyRight = limit(1);
    if (bodyRight - bodyLeft < minBodyWidth) continue;
    const outer = outerLimits();
    const left = outer.first === null ? bodyLeft : Math.min(bodyLeft, outer.first);
    const right = outer.last === null ? bodyRight : Math.max(bodyRight, outer.last);
    const renderedY = photoTop + (sourceY + 0.5) * scale;
    rows.push({ sourceY, renderedY, left, right,
      renderedLeft: photoLeft + (left + 0.5) * scale,
      renderedRight: photoLeft + (right + 0.5) * scale });
  }
  if (!rows.length) return clear;

  // Evaluate only straight upper-body edges VISIBLE INSIDE the final frame.
  // A source image boundary, even a very straight one, is NOT sufficient
  // when zooming puts it on or outside the finished portrait border.
  // The 2026-10-13 screenshot has a ~17-18px grey gap beside an almost
  // perfectly vertical right silhouette edge from y=530 to y=735 of the UI.
  // Excluding the outer 3.5% (21px at 605px) masked this actual cutoff.
  // Keep only a narrow anti-alias/contact zone, then demand the same long,
  // nearly-vertical internal edge evidence as before.
  const frameMargin = Math.max(3, canvasWidth * 0.015);
  const tolerance = Math.max(1.5, Math.min(3.6, canvasWidth * 0.005));
  const minimumSpan = Math.max(18, canvasHeight * 0.075, drawnFaceHeight * 0.82);
  const latestStart = Math.min(
    canvasHeight * 0.74,
    drawnFaceBottom + drawnFaceHeight * 1.65,
  );
  const faceOffset = Math.max(canvasWidth * 0.06, drawnFaceHeight * 0.40);

  function visibleBoundary(row, edge) {
    const x = row[edge];
    return row.renderedY >= 0 && row.renderedY < canvasHeight * 0.98
      && x > frameMargin && x < canvasWidth - frameMargin
      && Math.abs(x - drawnFaceCenter) >= faceOffset;
  }

  function hasVisibleStraightCut(side) {
    const edge = side === "left" ? "renderedLeft" : "renderedRight";
    for (let i = 0; i < rows.length; i++) {
      const initial = rows[i];
      if (initial.renderedY > latestStart) break;
      if (!visibleBoundary(initial, edge)) continue;
      const referenceX = initial[edge];
      let matches = 0;
      let misses = 0;
      let lastMatchY = initial.renderedY;
      for (let j = i; j < rows.length; j++) {
        const sample = rows[j];
        if (sample.renderedY >= canvasHeight * 0.98) break;
        if (visibleBoundary(sample, edge)
          && Math.abs(sample[edge] - referenceX) <= tolerance) {
          matches++;
          lastMatchY = sample.renderedY;
          misses = 0;
        } else if (++misses > 2) break;
      }
      const span = lastMatchY - initial.renderedY;
      const step = sourceStep * scale;
      if (span >= minimumSpan
        && matches >= Math.max(8, Math.ceil((span / step + 1) * 0.82))) return true;
    }
    return false;
  }

  const sides = [];
  if (hasVisibleStraightCut("left")) sides.push("left");
  if (hasVisibleStraightCut("right")) sides.push("right");
  return { warning: sides.length > 0, sides };
}
